import api from './api';

export interface BillItem {
  inventoryId: string;
  itemName: string;
  itemCode: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
  discount?: number;
  tax?: number;
}

export interface CreateRetailBill {
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  customerAddress?: string;
  items: BillItem[];
  subtotal: number;
  totalDiscount?: number;
  totalTax?: number;
  grandTotal: number;
  paymentMethod?: 'cash' | 'card' | 'upi' | 'net_banking' | 'wallet';
  paymentStatus?: 'paid' | 'unpaid' | 'partial';
  paidAmount?: number;
  paymentReference?: string;
  notes?: string;
}

export interface PaymentLog {
  _id: string;
  amount: number;
  paymentMethod: 'cash' | 'card' | 'upi' | 'net_banking' | 'wallet';
  paymentReference?: string;
  notes?: string;
  createdBy: string;
  createdAt: string;
}

export interface RetailBill extends CreateRetailBill {
  _id: string;
  billNumber: string;
  sellerId: string;
  createdBy: string;
  paymentLogs?: PaymentLog[];
  createdAt: string;
  updatedAt: string;
}

const retailBillService = {
  create: (data: CreateRetailBill) =>
    api.post<RetailBill>('/retail-bills', data).then((res) => res.data),

  getAll: (params?: {
    page?: number;
    limit?: number;
    paymentStatus?: string;
    customerPhone?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
    if (params?.customerPhone) queryParams.append('customerPhone', params.customerPhone);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    return api
      .get(`/retail-bills?${queryParams.toString()}`)
      .then((res) => res.data);
  },

  getById: (id: string) =>
    api.get<RetailBill>(`/retail-bills/${id}`).then((res) => res.data),

  update: (id: string, data: Partial<CreateRetailBill>) =>
    api.patch<RetailBill>(`/retail-bills/${id}`, data).then((res) => res.data),

  delete: (id: string) =>
    api.delete(`/retail-bills/${id}`).then((res) => res.data),

  downloadPDF: (id: string) =>
    api
      .get(`/retail-bills/${id}/pdf`, { responseType: 'blob' })
      .then((res) => res.data),

  getStats: () =>
    api.get('/retail-bills/stats').then((res) => res.data),

  // Payment log endpoints
  addPayment: (billId: string, data: {
    amount: number;
    paymentMethod: 'cash' | 'card' | 'upi' | 'net_banking' | 'wallet';
    paymentReference?: string;
    notes?: string;
  }) =>
    api.post<RetailBill>(`/retail-bills/${billId}/payments`, data).then((res) => res.data),

  getPaymentLogs: (billId: string) =>
    api.get<PaymentLog[]>(`/retail-bills/${billId}/payments`).then((res) => res.data),
};

export default retailBillService;
